import classNames from 'classnames';
import { useState } from 'react';
import { getRowPackageLayout } from '..';
import { INumber } from '../../../../model';
import {
  getCollapseIndexStep,
  getColumnWidth,
  isMultiHeaderCollapse,
} from '../../libs';
import { ITableColumnMultiHeader } from '../../model';

interface IProps<T> {
  readonly isRowSelection?: boolean;
  readonly multiRowsHeader?: ReadonlyArray<ITableColumnMultiHeader<T>>;
  readonly onChangedCollapseIndex?: (
    index: INumber<ReadonlyArray<number>>
  ) => void;
  readonly enabledCollapse?: boolean;
  readonly collapseIndex: INumber<ReadonlyArray<number>>;
}

/**
 * @description 这不是一个真正的多级表头实现，它是残缺的，简单的扩展出多级表头，纯展示，不做点击交互
 * @param props
 */
export function IncompleteMultiLevelHeaderComponent<T>(props: IProps<T>) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const getTitle = (isCollapse: boolean) => {
    if (props.enabledCollapse) {
      if (isCollapse) {
        return '展开';
      }
      return '折叠';
    }
    return '';
  };

  const mergeCollapseIndex = (index: number, colSpan: number) => {
    if (!props.onChangedCollapseIndex) {
      return;
    }
    const values = Object.assign({}, props.collapseIndex);
    if (isMultiHeaderCollapse(props.collapseIndex, index)) {
      values[index] = [];
    } else {
      values[index] = getCollapseIndexStep(
        index,
        colSpan,
        props.multiRowsHeader
      );
    }
    props.onChangedCollapseIndex(Object.assign({}, values));
  };

  const onDoubleClick = (item: ITableColumnMultiHeader<T>, index: number) => {
    return () => {
      if (props.enabledCollapse && typeof item.colSpan !== 'undefined') {
        mergeCollapseIndex(index, item.colSpan);
      }
    };
  };

  const onDragStart = (ev: any, index: number) => {
    setDragIndex(index);
    ev.stopPropagation();
  };

  const onDragEnd = (ev: any) => {
    setDragIndex(null);
    setDragOverIndex(null);
    ev.stopPropagation();
  };

  const onDragOver = (ev: any, index: number) => {
    if (dragIndex !== index) {
      setDragOverIndex(index);
    }
    ev.preventDefault();
  };

  const onDrop = (ev: any, item: ITableColumnMultiHeader<T>, index: number) => {
    if (index !== dragIndex) {
      // 交换数据
      if (item.dragConfig?.onDrop && dragIndex) {
        item.dragConfig.onDrop(props?.multiRowsHeader || [], dragIndex, index);
      }
    }
    setDragIndex(null);
    setDragOverIndex(null);
    ev.preventDefault();
  };

  const renderRow = (item: ITableColumnMultiHeader<T>, index: number) => {
    if (item.colSpan === 0) {
      return null;
    }
    const draggable = !!item?.dragConfig?.draggable;
    const collapsed = isMultiHeaderCollapse(props.collapseIndex, index);
    const style = Object.assign(
      {},
      getColumnWidth(item.width),
      draggable
        ? {
            cursor: 'move',
          }
        : {}
    );
    const className = classNames(
      'incomplete-column-item',
      `incomplete-column-item-${index}`,
      item.className,
      {
        collapsed,
        'column-collapse': props.enabledCollapse,
      },
      index - 1 === dragIndex && dragOverIndex !== null && draggable
        ? 'dragging'
        : '',
      index - 1 === dragOverIndex && dragOverIndex !== null && draggable
        ? 'dragging-over'
        : ''
    );
    const colSpan = collapsed ? 1 : item.colSpan;
    return (
      <th
        className={className}
        align={item.align}
        key={index}
        colSpan={colSpan}
        style={style}
        title={getTitle(collapsed)}
        onDoubleClick={onDoubleClick(item, index)}
        draggable={draggable}
        onDragStart={(ev) => onDragStart(ev, index - 1)}
        onDrop={(ev) => onDrop(ev, item, index - 1)}
        onDragOver={(ev) => onDragOver(ev, index - 1)}
        onDragEnd={(ev) => onDragEnd(ev)}
      >
        {getRowPackageLayout(item.title)}
      </th>
    );
  };

  const renderTH = () => {
    return props.multiRowsHeader?.map((item, index) => {
      return renderRow(item, index + 1);
    });
  };

  if (!props.multiRowsHeader?.length) {
    return null;
  }

  return (
    <tr>
      {props.isRowSelection ? <th></th> : null}
      {renderTH()}
    </tr>
  );
}
